import {
  Case,
  CaseClinicalSpecialistResource,
  CaseOCSConsoleResource,
  CaseResource,
  CaseResourceType,
} from '@/api/apis/case';
import { EmployeeComposed, OcsConsoleData } from '@/api/apis/resources';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { isCaseTrial } from '@/utils/helpers';

type ResourceMismatchWarning = {
  id: string;
  message: string;
};

type ClinicalSpecialistWithTrialPrograms = EmployeeComposed & {
  trialPrograms?: TrialProgram[] | null;
};

type OcsConsoleWithTrialProgram = OcsConsoleData & {
  trialProgram?: TrialProgram | null;
};

type TrialProgramLookups = {
  clinicalSpecialistTrialProgramsById?: Record<string, TrialProgram[]>;
  ocsConsoleTrialProgramsById?: Record<string, TrialProgram[]>;
};

const SUPPORTED_TRIAL_PROGRAMS: TrialProgram['name'][] = ['DENOVO', 'ENHANCE'];

const isSupportedTrialProgram = (trialProgram?: TrialProgram | null): boolean => {
  if (!trialProgram) return false;

  return SUPPORTED_TRIAL_PROGRAMS.includes(trialProgram.name);
};

const isSameTrialProgram = (first?: TrialProgram | null, second?: TrialProgram | null): boolean => {
  if (!first || !second) return false;

  return first.id === second.id;
};

const getClinicalSpecialistData = (
  resource: CaseClinicalSpecialistResource,
): ClinicalSpecialistWithTrialPrograms | null => {
  return resource.data ?? (resource.resourceData as ClinicalSpecialistWithTrialPrograms | undefined) ?? null;
};

const getOcsDeviceData = (resource: CaseResource<CaseOCSConsoleResource>): OcsConsoleWithTrialProgram | null => {
  return resource.data ?? (resource.resourceData as OcsConsoleWithTrialProgram | undefined) ?? null;
};

const getClinicalSpecialistTrialPrograms = (
  resource: CaseClinicalSpecialistResource,
  lookups?: TrialProgramLookups,
): TrialProgram[] => {
  const lookupValue = lookups?.clinicalSpecialistTrialProgramsById?.[resource.resourceId];

  if (lookupValue) {
    return lookupValue;
  }

  return getClinicalSpecialistData(resource)?.trialPrograms ?? [];
};

const getOcsDeviceTrialPrograms = (
  resource: CaseResource<CaseOCSConsoleResource>,
  lookups?: TrialProgramLookups,
): TrialProgram[] => {
  const lookupValue = lookups?.ocsConsoleTrialProgramsById?.[resource.resourceId];

  if (lookupValue) {
    return lookupValue;
  }

  const trialProgram = getOcsDeviceData(resource)?.trialProgram;

  return trialProgram ? [trialProgram] : [];
};

const hasSupportedTrialCertification = (
  resource: CaseClinicalSpecialistResource,
  lookups?: TrialProgramLookups,
): boolean => {
  const trialPrograms = getClinicalSpecialistTrialPrograms(resource, lookups);

  if (!trialPrograms.length) {
    return false;
  }

  return trialPrograms.some(isSupportedTrialProgram);
};

const isTrialSpecificDevice = (
  resource: CaseResource<CaseOCSConsoleResource>,
  lookups?: TrialProgramLookups,
): boolean => {
  const trialPrograms = getOcsDeviceTrialPrograms(resource, lookups);

  if (!trialPrograms.length) {
    return false;
  }

  return trialPrograms.some(isSupportedTrialProgram);
};

const isClinicalSpecialistCertifiedForCase = (
  resource: CaseClinicalSpecialistResource,
  caseData: Case,
  lookups?: TrialProgramLookups,
): boolean | null => {
  const caseTrialProgram = caseData.trialProgram;
  const trialPrograms = getClinicalSpecialistTrialPrograms(resource, lookups);

  if (!caseTrialProgram) {
    return null;
  }

  if (!trialPrograms.length) {
    return false;
  }

  return trialPrograms.some((program) => isSameTrialProgram(program, caseTrialProgram));
};

const isTrialSpecificDeviceForCase = (
  resource: CaseResource<CaseOCSConsoleResource>,
  caseData: Case,
  lookups?: TrialProgramLookups,
): boolean | null => {
  const caseTrialProgram = caseData.trialProgram;
  const trialPrograms = getOcsDeviceTrialPrograms(resource, lookups);

  if (!caseTrialProgram) {
    return null;
  }

  if (!trialPrograms.length) {
    return false;
  }

  return trialPrograms.some((program) => isSameTrialProgram(program, caseTrialProgram));
};

export const getClinicalResourceMismatchWarnings = (
  caseData: Case,
  lookups?: TrialProgramLookups,
): ResourceMismatchWarning[] => {
  const isTrial = Boolean(
    caseData.trialProgram &&
    isSupportedTrialProgram(caseData.trialProgram) &&
    isCaseTrial(caseData.organType, caseData.trialProgram, caseData.questionnaire),
  );

  const clinicalSpecialists = caseData.resources.filter(
    (resource): resource is CaseClinicalSpecialistResource =>
      resource.resourceType === CaseResourceType.ClinicalSpecialist,
  );

  const ocsConsoles = caseData.resources.filter(
    (resource): resource is CaseResource<CaseOCSConsoleResource> =>
      resource.resourceType === CaseResourceType.OCSConsole,
  );

  const warnings: ResourceMismatchWarning[] = [];

  if (!isTrial) {
    const hasTrialCertifiedCS = clinicalSpecialists.some((resource) =>
      hasSupportedTrialCertification(resource, lookups),
    );

    const hasTrialSpecificDevice = ocsConsoles.some((resource) => isTrialSpecificDevice(resource, lookups));

    if (hasTrialCertifiedCS) {
      warnings.push({
        id: 'trial-certified-cs-on-regular-case',
        message: 'Clinical Specialist with trial certification is assigned to the regular case.',
      });
    }

    if (hasTrialSpecificDevice) {
      warnings.push({
        id: 'trial-device-on-regular-case',
        message: 'Trial specific OCS device is assigned to the regular case.',
      });
    }

    return warnings;
  }

  const csChecks = clinicalSpecialists
    .map((resource) => isClinicalSpecialistCertifiedForCase(resource, caseData, lookups))
    .filter((value): value is boolean => value !== null);

  const deviceChecks = ocsConsoles
    .map((resource) => isTrialSpecificDeviceForCase(resource, caseData, lookups))
    .filter((value): value is boolean => value !== null);

  const hasRegularCS = csChecks.some((value) => !value);
  const hasRegularDevice = deviceChecks.some((value) => !value);

  if (hasRegularCS) {
    warnings.push({
      id: 'regular-cs-on-trial-case',
      message: 'Clinical Specialist without trial certification is assigned to the trial case.',
    });
  }

  if (hasRegularDevice) {
    warnings.push({
      id: 'regular-device-on-trial-case',
      message: 'Regular OCS device is assigned to the trial case.',
    });
  }

  return warnings;
};
